import { writeDesktopSessionDurably } from '@/desktop/desktopSessionLazy';
import { effectiveDeploymentAddress } from '@/features/onboarding/deploymentAddress';
import { writeShellSessionDurably } from '@/shell/shellSessionVault';
import { RefreshSecretNotStoredError } from './refreshSecretVault';

/**
 * T-0666 (ADR-100's T-0666 amendment): the write-ahead chain, mirrored into the native vault before
 * a request names its newest candidate.
 *
 * In the desktop and the iOS shell the webview's storage is not durable: WKWebView keeps no
 * `localStorage` for `tauri://localhost`, and an iOS web view loses it with its website data. After a
 * lost answer and such a reset, the hydration used to restore the vault's current secret alone,
 * which the deployment had already rotated past, and the next refresh revoked the chain. With the
 * candidates in the vault too, the hydration restores the whole chain and recovery presents the
 * newest first.
 *
 * ⭐ **The whole chain, in one write** — the access token, the current secret and every candidate.
 * Never the candidates alone: if an earlier fire-and-forget mirror had been lost, a candidates-only
 * write would pair a retired current secret with the new candidate.
 *
 * ⛔ **A vault that would not keep it is a store that would not keep the candidate.** Rejects with
 * `RefreshSecretNotStoredError`, so `rotateWriteAhead` names nothing (ADR-096 §1). Resolves at once
 * outside a shell, where there is no native vault.
 */
export interface IRefreshChain {
  accessToken: string;
  /** The current secret. */
  refreshToken: string;
  /** The candidates, oldest first. */
  pendingRefreshTokens: string[];
}

export async function mirrorRefreshChain(chain: IRefreshChain): Promise<void> {
  const tokens = {
    accessToken: chain.accessToken,
    refreshToken: chain.refreshToken,
    pendingRefreshTokens: [...chain.pendingRefreshTokens],
  };
  const [desktopKept, shellKept] = await Promise.all([
    writeDesktopSessionDurably(effectiveDeploymentAddress(), tokens),
    writeShellSessionDurably(tokens),
  ]);
  if (!desktopKept || !shellKept) {
    throw new RefreshSecretNotStoredError(new Error('The native session vault refused the chain.'));
  }
}
